import {
  deviceSvg,
  panelSvg,
  productSvg,
  reportSvg,
  reviewSvg,
  userSvg,
} from 'constants/images';

import * as React from 'react';
import { useState } from 'react';

import * as MaterialIcons from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import useGgEnvironment from 'hooks/useGgEnvironment';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { isAfsWorkspace } from 'utils/afsUtils';
import { isAzure, isProd } from 'utils/environmentUtils';

import useStyles from './useStyles';
import {
  PANELS,
  PRODUCTS,
  USERS,
  SUPERADMIN_MANAGE_DEVICES,
  REPORTS_QA,
  REPORT_DASHBOARD,
  REQUEST_REPORT,
  CUSTOM_LEXICON,
  PROJECTS,
  PQ_MODEL_PLAYGROUND,
  NOTIFICATION,
  FILE_STORAGE,
} from '../../constants/routePaths';
import ProductFolderComponent from '../../containers/ProductFolder';
import formatPath from '../../utils/formatPath';

interface Props {
  className?: string;
  workspaceProducerId?: number;
  viewerRoleIsAdmin: boolean;
  viewerRoleIsSuperadmin: boolean;
  viewerRoleIsWorkspaceAdmin: boolean;
  viewerRoleIsPanelManage: boolean;
  viewerRoleIsTextureAnalysis: boolean;
  viewerRoleIsAnalysisView: boolean;
  viewerRoleIsProductReview: boolean;
}

const renderItems = (classes, items, active, setActive) =>
  items.map((item) => {
    const Icon = () => {
      switch (item.iconName) {
        case 'productSvg':
          return (
            <img className={classes.icon} src={productSvg} alt="product-icon" />
          );

        case 'panelSvg':
          return (
            <img className={classes.icon} src={panelSvg} alt="panel-icon" />
          );

        case 'userSvg':
          return <img className={classes.icon} src={userSvg} alt="user-icon" />;

        case 'reportSvg':
          return (
            <img className={classes.icon} src={reportSvg} alt="report-icon" />
          );

        case 'reviewSvg':
          return (
            <img className={classes.icon} src={reviewSvg} alt="review-icon" />
          );

        case 'deviceSvg':
          return (
            <img className={classes.icon} src={deviceSvg} alt="device-icon" />
          );

        default: {
          const Icon = MaterialIcons[item.iconName];
          return <Icon className={classes.icon} />;
        }
      }
    };

    return (
      <Box key={item.path}>
        <NavLink
          activeClassName={classes.linkActive}
          className={classes.link}
          key={item.path}
          to={item.path}
          onClick={() => setActive(item)}
          isActive={(match) => {
            return !!match && !(match.url.includes('admin') && !match.isExact);
          }}
        >
          <Box className={classes.accessory} />
          {Icon()}
          <Box className={classes.text}>{item.text}</Box>
        </NavLink>

        {active.path === PRODUCTS && item.path === PRODUCTS && (
          <ProductFolderComponent />
        )}
      </Box>
    );
  });

const VerticalNavigationMenuContainer: React.FunctionComponent<Props> = ({
  className,
  workspaceProducerId,
  viewerRoleIsAdmin,
  viewerRoleIsSuperadmin,
  viewerRoleIsWorkspaceAdmin,
  viewerRoleIsPanelManage,
  viewerRoleIsTextureAnalysis,
  viewerRoleIsAnalysisView,
  viewerRoleIsProductReview,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(useTheme());
  const environ = useGgEnvironment();
  const IS_AZURE = isAzure(environ);

  const productMenuItem = {
    path: PRODUCTS,
    iconName: 'productSvg',
    text: t('navigation.products'),
  };

  const notificationMenuItem = {
    path: NOTIFICATION,
    iconName: 'reviewSvg',
    text: t('navigation.notification'),
  };
  const filestorageMenuItem = {
    path: FILE_STORAGE,
    iconName: 'reviewSvg',
    text: t('navigation.fileStorage'),
  };
  const [active, setActive] = useState(productMenuItem);

  const isAdminOrSuperadmin = viewerRoleIsAdmin || viewerRoleIsSuperadmin;

  const showReportsQa = isAdminOrSuperadmin && (
    isAfsWorkspace(workspaceProducerId) ||
    workspaceProducerId === 107 ||
    workspaceProducerId === 189 ||
    workspaceProducerId === 209
  );

  const showRequestReport = isAdminOrSuperadmin;

  const showFileStorage = IS_AZURE && isAdminOrSuperadmin;

  const showModelPlayground = !isProd(environ) && isAfsWorkspace(workspaceProducerId);

  const showProducts = viewerRoleIsPanelManage || viewerRoleIsSuperadmin;
  const showUsers = viewerRoleIsWorkspaceAdmin || viewerRoleIsSuperadmin;
  const showProjects = viewerRoleIsAdmin || viewerRoleIsAnalysisView || viewerRoleIsSuperadmin;

  const primaryMenuItems = [
    ...(showProducts ? [productMenuItem] : []),
    ...(showProducts
      ? [{ path: PANELS, iconName: 'panelSvg', text: t('navigation.panels') }]
      : []),
    ...(showUsers ? [{ path: USERS, iconName: 'userSvg', text: t('navigation.users') }]
      : []),
    ...(showProjects
      ? [
        {
          path: PROJECTS,
          iconName: 'panelSvg',
          text: t('navigation.projects'),
        },
      ]
      : []),
    ...(showReportsQa
      ? [
        {
          path: REPORTS_QA,
          iconName: 'reportSvg',
          text: t('navigation.reportsQa'),
        },
      ]
      : []),
    ...(isAdminOrSuperadmin
      ? [
        {
          path: SUPERADMIN_MANAGE_DEVICES,
          iconName: 'deviceSvg',
          text: t('navigation.devices'),
        },
      ]
      : []),
    ...(isAdminOrSuperadmin
      ? [
        {
          path: formatPath(REPORT_DASHBOARD, { reportId: null }),
          iconName: 'reportSvg',
          text: t('navigation.reportDashboard'),
        },
      ]
      : []),
    ...(showRequestReport
      ? [
        {
          path: REQUEST_REPORT,
          iconName: 'reportSvg',
          text: t('reports.reportRequest'),
        },
      ]
      : []),
    ...(viewerRoleIsSuperadmin
      ? [
        {
          path: CUSTOM_LEXICON,
          iconName: 'reviewSvg',
          text: t('navigation.customLexicons'),
        },
      ]
      : []),
    // disable PQ Model Playground in prod for now
    ...(showModelPlayground
      ? [
        {
          path: PQ_MODEL_PLAYGROUND,
          iconName: 'reviewSvg',
          text: t('navigation.modelPlayground'),
        },
      ]
      : []),
    ...[notificationMenuItem],
    ...(showFileStorage ? [filestorageMenuItem] : []),
  ];

  return (
    <div className={className}>
      {renderItems(classes, primaryMenuItems, active, setActive)}
    </div>
  );
};

VerticalNavigationMenuContainer.displayName = 'VerticalNavigationMenuContainer';

export default VerticalNavigationMenuContainer;
